import { cn } from '@sistemas/ui'
import * as React from 'react'

import { useAvatarUrl } from '@/lib/avatars'

export interface AvatarPerson {
  readonly fullName: string
  readonly initials: string
  readonly avatarPath?: string | undefined
  /**
   * Foto de fora do Hub (a do GitHub, para autor de PR que não é membro ou não
   * tem foto aqui). Só entra quando não há `avatarPath` resolvido: a foto que a
   * pessoa escolheu no Hub vence.
   */
  readonly imageUrl?: string | undefined
}

const SIZE = {
  xs: 'h-4 w-4 text-[8px]',
  sm: 'h-5 w-5 text-[9px]',
  md: 'h-6 w-6 text-[10px]',
  lg: 'h-8 w-8 text-[11px]',
  xl: 'h-20 w-20 text-[22px]',
} as const

export type AvatarSize = keyof typeof SIZE

/**
 * Foto da pessoa, com as iniciais no lugar enquanto a foto não carrega ou
 * quando não há foto. O `title` fica sempre: duas pessoas "GS" no time
 * acontecem, e a foto pequena também não basta para todo mundo.
 */
export function Avatar({
  person,
  size = 'sm',
  className,
  showTitle = true,
}: {
  person: AvatarPerson
  size?: AvatarSize
  className?: string
  showTitle?: boolean
}) {
  const url = useAvatarUrl(person.avatarPath) ?? person.imageUrl
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => setFailed(false), [url])

  return (
    <span
      title={showTitle ? person.fullName : undefined}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-primary/15 font-semibold text-primary',
        SIZE[size],
        className,
      )}
    >
      {url && !failed ? (
        <img
          src={url}
          alt={person.fullName}
          draggable={false}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden={showTitle}>{person.initials}</span>
      )}
    </span>
  )
}

/**
 * Várias pessoas empilhadas, a mais recente por cima. Passou de `max`, o
 * resto vira "+N" — o número importa (quantos trabalharam), os rostos
 * seguintes não cabem.
 */
export function AvatarStack({
  people,
  max = 4,
  size = 'sm',
  className,
}: {
  people: readonly AvatarPerson[]
  max?: number
  size?: AvatarSize
  className?: string
}) {
  if (people.length === 0) return null
  const shown = people.slice(0, max)
  const rest = people.length - shown.length
  const names = people.map((p) => p.fullName).join(', ')

  return (
    <span
      className={cn('inline-flex items-center', className)}
      title={names}
      aria-label={`${people.length} pessoa${people.length === 1 ? '' : 's'}: ${names}`}
    >
      {shown.map((person, i) => (
        <Avatar
          key={`${person.fullName}-${i}`}
          person={person}
          size={size}
          showTitle={false}
          className={cn('ring-2 ring-card', i > 0 && '-ml-1.5')}
        />
      ))}
      {rest > 0 && (
        <span
          className={cn(
            '-ml-1.5 inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-mono text-muted-foreground ring-2 ring-card',
            SIZE[size],
          )}
        >
          +{rest}
        </span>
      )}
    </span>
  )
}
